import { CALENDAR_BUILDER_FEATURE_GROUP } from '../features/crm-integrations/calendar-builder/group';
import { CHECKOUTS_FEATURE_GROUP } from '../features/crm-integrations/checkouts/group';
import { DATA_BRIDGE_FEATURE_GROUP } from '../features/crm-integrations/data-bridge/group';
import { DEDUPER_FEATURE_GROUP } from '../features/crm-integrations/deduper/group';
import { DEVELOPMENT_DATA_IMPORTS_FEATURE_GROUP } from '../features/crm-integrations/development/data-imports/group';
import { DEVELOPMENT_LINK2PAY_FEATURE_GROUP } from '../features/crm-integrations/development/link2pay/group';
import { DEVELOPMENT_MEMBERSHIP_CARD_DESIGNER_FEATURE_GROUP } from '../features/crm-integrations/development/membership-card-designer/group';
import { DEVELOPMENT_FEATURE_GROUP } from '../features/crm-integrations/development/group';
import { DEVELOPMENT_TICKET_DESIGNER_FEATURE_GROUP } from '../features/crm-integrations/development/ticket-designer/group';
import { EMAIL_DESIGNER_FEATURE_GROUP } from '../features/crm-integrations/email-designer/group';
import { TICKET_SCANNERS_FEATURE_GROUP } from '../features/crm-integrations/ticket-scanners/group';
import { ACTIVITY_FEATURE_GROUP } from '../features/administrative';
import { API_KEYS_FEATURE_GROUP } from '../features/administrative';
import { ASSETS_FEATURE_GROUP } from '../features/administrative';
import { DOMAINS_FEATURE_GROUP } from '../features/administrative';
import { ORGANIZATION_FEATURE_GROUP } from '../features/administrative';
import { RESOURCES_FEATURE_GROUP } from '../features/administrative';
import { ROLES_FEATURE_GROUP } from '../features/administrative';
import { SECURITY_FEATURE_GROUP } from '../features/administrative';
import { TEAM_FEATURE_GROUP } from '../features/administrative';
import { PAYMENTS_ACTIVITY_FEATURE_GROUP } from '../features/payments/activity/group';
import { PAYMENTS_GATEWAYS_FEATURE_GROUP } from '../features/payments/gateways/group';
import { PAYMENTS_INTEGRATIONS_FEATURE_GROUP } from '../features/payments/integrations/group';
import { PAYMENTS_SETTINGS_FEATURE_GROUP } from '../features/payments/settings/group';
import { PAYMENTS_TAKE_PAYMENT_FEATURE_GROUP } from '../features/payments/take-payment/group';
import { PAYMENTS_TRANSACTIONS_FEATURE_GROUP } from '../features/payments/transactions/group';

import type { FeatureProduct } from '../types';

export type PlanLevel = 'growth' | 'standard' | 'professional' | 'expert';

export type ProductCategory = 'audience' | 'money' | 'frontOfHouse' | 'platform';

export const productCategories: { key: ProductCategory; label: string }[] = [
  { key: 'audience', label: 'Audience' },
  { key: 'money', label: 'Money' },
  { key: 'frontOfHouse', label: 'Front of house' },
  { key: 'platform', label: 'Platform' },
];

/**
 * One job a product exists to get done, and the path inside that product where it is done.
 * The switcher shows these under the heading Actions; jobs-to-be-done is the internal name.
 * A product declares them in the order its own left navigation lists their destinations.
 *
 * The key is what a favourite is stored against, so it outlives any move of the path: the
 * routes under it have been renamed before and a stored shortcut must survive the next one.
 *
 * Every job names the feature group that owns its destination, so the offer and the door it opens
 * are gated by the same thing whichever product declares it.
 */
export type ProductJob = { key: string; label: string; path: string; groupGuid: string };

/**
 * One application a product opens onto, and where it lives. A product's applications and its jobs
 * share a token namespace, so a key is unique across both lists — `product-catalog.spec.ts` pins it.
 *
 * `icon` is a Material Symbols name the client maps to a drawn icon; `key` is what a favourite is
 * stored against AND what a server-rendered page tags itself with, so it outlives a path move.
 */
export type ProductApplication = { key: string; label: string; path: string; icon: string; groupGuid: string };

export const productCatalog: Record<
  FeatureProduct,
  {
    label: string;
    description: string;
    weight: number;
    plans: PlanLevel[];
    category?: ProductCategory;
    badge?: string;
    jobs?: ProductJob[];
    applications?: ProductApplication[];
  }
> = {
  account: {
    label: 'Account',
    description: 'Set up your organization, team, roles and security.',
    weight: 0,
    plans: [],
    category: 'platform',
    badge: 'AC',
    jobs: [
      { key: 'organization', label: 'Add organization details', path: '/organization', groupGuid: ORGANIZATION_FEATURE_GROUP.guid },
      { key: 'assets', label: 'Upload assets', path: '/assets', groupGuid: ASSETS_FEATURE_GROUP.guid },
      { key: 'team', label: 'Invite team', path: '/team', groupGuid: TEAM_FEATURE_GROUP.guid },
      { key: 'roles', label: 'Create a custom role', path: '/roles/roles', groupGuid: ROLES_FEATURE_GROUP.guid },
      { key: 'activity', label: 'Review recent activity', path: '/activity', groupGuid: ACTIVITY_FEATURE_GROUP.guid },
      { key: 'domains', label: 'Add a custom domain name', path: '/domains', groupGuid: DOMAINS_FEATURE_GROUP.guid },
      { key: 'apiKeys', label: 'Create an API key', path: '/keys', groupGuid: API_KEYS_FEATURE_GROUP.guid },
      { key: 'security', label: 'Configure security', path: '/security', groupGuid: SECURITY_FEATURE_GROUP.guid },
      { key: 'resources', label: 'Access training resources', path: '/resources', groupGuid: RESOURCES_FEATURE_GROUP.guid },
    ],
  },
  app: {
    label: 'Altru integrations',
    description: 'Sync, clean and move your Altru data between tools.',
    weight: 5,
    plans: ['growth', 'standard', 'professional', 'expert'],
    category: 'platform',
    badge: 'AI',
    jobs: [
      {
        key: 'dedupers',
        label: 'Clean duplicate records',
        path: '/dedupers',
        groupGuid: DEDUPER_FEATURE_GROUP.guid,
      },
      {
        key: 'calendars',
        label: 'Build an event calendar',
        path: '/calendars',
        groupGuid: CALENDAR_BUILDER_FEATURE_GROUP.guid,
      },
      {
        key: 'emails',
        label: 'Design a branded email',
        path: '/emails',
        groupGuid: EMAIL_DESIGNER_FEATURE_GROUP.guid,
      },
      {
        key: 'scanners',
        label: 'Set up a ticket scanner',
        path: '/scanners',
        groupGuid: TICKET_SCANNERS_FEATURE_GROUP.guid,
      },
      {
        key: 'checkoutSetup',
        label: 'Set up a checkout',
        path: '/v1/checkouts',
        groupGuid: CHECKOUTS_FEATURE_GROUP.guid,
      },
      {
        key: 'membershipCards',
        label: 'Design a membership card',
        path: '/development/memberships',
        groupGuid: DEVELOPMENT_MEMBERSHIP_CARD_DESIGNER_FEATURE_GROUP.guid,
      },
      {
        key: 'eTickets',
        label: 'Design an eTicket',
        path: '/development/tickets',
        groupGuid: DEVELOPMENT_TICKET_DESIGNER_FEATURE_GROUP.guid,
      },
      {
        key: 'linksToPay',
        label: 'Send a payment link',
        path: '/development/links-to-pay',
        groupGuid: DEVELOPMENT_LINK2PAY_FEATURE_GROUP.guid,
      },
      {
        key: 'imports',
        label: 'Import records into Altru',
        path: '/development/imports',
        groupGuid: DEVELOPMENT_DATA_IMPORTS_FEATURE_GROUP.guid,
      },
      {
        key: 'dataBridges',
        label: 'Connect an integration',
        path: '/data-bridges',
        groupGuid: DATA_BRIDGE_FEATURE_GROUP.guid,
      },
    ],
    applications: [
      {
        key: 'deduper',
        label: 'Deduper',
        path: '/dedupers',
        icon: 'layers',
        groupGuid: DEDUPER_FEATURE_GROUP.guid,
      },
      {
        key: 'calendar-builder',
        label: 'Calendar Builder',
        path: '/calendars',
        icon: 'event',
        groupGuid: CALENDAR_BUILDER_FEATURE_GROUP.guid,
      },
      {
        key: 'email-designer',
        label: 'Email Designer',
        path: '/emails',
        icon: 'email',
        groupGuid: EMAIL_DESIGNER_FEATURE_GROUP.guid,
      },
      {
        key: 'ticket-scanner',
        label: 'Ticket Scanners',
        path: '/scanners',
        icon: 'qr_code_scanner',
        groupGuid: TICKET_SCANNERS_FEATURE_GROUP.guid,
      },
      {
        key: 'checkouts',
        label: 'Checkouts',
        path: '/v1/checkouts',
        icon: 'shopping_cart',
        groupGuid: CHECKOUTS_FEATURE_GROUP.guid,
      },
      {
        key: 'development',
        label: 'Development',
        path: '/development',
        icon: 'group',
        groupGuid: DEVELOPMENT_FEATURE_GROUP.guid,
      },
      {
        key: 'data-bridge',
        label: 'Data Bridge',
        path: '/data-bridges',
        icon: 'insert_chart_outlined',
        groupGuid: DATA_BRIDGE_FEATURE_GROUP.guid,
      },
    ],
  },
  crm: {
    label: 'CRM',
    description: 'See every constituent, their history and their relationships.',
    weight: 1,
    plans: ['growth'],
    category: 'audience',
    badge: 'CR',
  },
  payments: {
    label: 'Payments',
    description: 'Take payments and reconcile every transaction.',
    weight: 2,
    plans: ['growth'],
    category: 'money',
    badge: 'PY',
    jobs: [
      { key: 'transactions', label: 'Review transactions', path: '/transactions/all', groupGuid: PAYMENTS_TRANSACTIONS_FEATURE_GROUP.guid },
      { key: 'disputes', label: 'Resolve a dispute', path: '/transactions/disputes', groupGuid: PAYMENTS_TRANSACTIONS_FEATURE_GROUP.guid },
      { key: 'suspicious', label: 'Check suspicious transactions', path: '/transactions/suspicious', groupGuid: PAYMENTS_TRANSACTIONS_FEATURE_GROUP.guid },
      { key: 'payment', label: 'Take a payment', path: '/payments/payment', groupGuid: PAYMENTS_TAKE_PAYMENT_FEATURE_GROUP.guid },
      { key: 'gateways', label: 'Connect a gateway', path: '/gateways/all', groupGuid: PAYMENTS_GATEWAYS_FEATURE_GROUP.guid },
      { key: 'fees', label: 'Review gateway fees', path: '/gateways/fees', groupGuid: PAYMENTS_GATEWAYS_FEATURE_GROUP.guid },
      { key: 'integrations', label: 'Add an integration', path: '/integrations/all', groupGuid: PAYMENTS_INTEGRATIONS_FEATURE_GROUP.guid },
      { key: 'activity', label: 'Review recent activity', path: '/activity', groupGuid: PAYMENTS_ACTIVITY_FEATURE_GROUP.guid },
      { key: 'email', label: 'Edit the receipt email', path: '/settings/email', groupGuid: PAYMENTS_SETTINGS_FEATURE_GROUP.guid },
    ],
  },
  tickets: {
    label: 'Tickets',
    description: 'Sell and scan tickets for admission, tours and events.',
    weight: 4,
    plans: ['growth'],
    category: 'frontOfHouse',
    badge: 'TK',
  },
  marketing: {
    label: 'Marketing',
    description: 'Build campaigns and send email to the right audience.',
    weight: 3,
    plans: ['growth'],
    category: 'audience',
    badge: 'MK',
  },
  all: {
    label: 'All Products',
    description: 'Cross-product features.',
    weight: 0,
    plans: [],
  },
};

export function getProductLabel(product: string): string {
  return productCatalog[product as FeatureProduct]?.label ?? product;
}

export function getProductDescription(product: string): string | undefined {
  return productCatalog[product as FeatureProduct]?.description;
}

export function getProductWeight(product: string): number {
  return productCatalog[product as FeatureProduct]?.weight ?? 0;
}

export function getProductBadge(product: string): string {
  return productCatalog[product as FeatureProduct]?.badge ?? product.slice(0, 2).toUpperCase();
}

export function getProductJobs(product: string): ProductJob[] {
  return productCatalog[product as FeatureProduct]?.jobs ?? [];
}

export function getProductApplications(product: string): ProductApplication[] {
  return productCatalog[product as FeatureProduct]?.applications ?? [];
}

export function requiresEntitlement(product: string): boolean {
  const entry = productCatalog[product as FeatureProduct];
  return entry !== undefined && entry.plans.length > 0;
}

export function getProductCategory(product: string): ProductCategory | undefined {
  return productCatalog[product as FeatureProduct]?.category;
}

export function getSwitchableProducts(): FeatureProduct[] {
  const names = Object.keys(productCatalog) as FeatureProduct[];
  const switchable = names.filter((name) => productCatalog[name].category !== undefined);
  return switchable.sort((left, right) => productCatalog[left].weight - productCatalog[right].weight);
}
